/**
 * HOOK: useUsersManagement (VERSION REFACTORISÉE)
 *
 * ⚠️ NOTE: Ce hook est pour la GESTION des utilisateurs (admin uniquement)
 * Pour accéder au cache des utilisateurs, utilisez useUsers() du contexte
 *
 * ⚠️ NOTE: Ce hook ne peut pas utiliser useCRUD car il utilise des fonctions RPC PostgreSQL
 * au lieu de CRUD standard. Cependant, la structure a été améliorée pour plus de clarté.
 *
 * 🎯 Avant: 198 lignes | Après: 148 lignes | Réduction: 25%
 */

import { useState, useCallback, useMemo } from 'react';
import type { User, UserRole } from '../types/user';
import { useSupabase } from './useSupabase';
import { useUsers as useUsersCache } from '../contexts/UsersContext';

export function useUsersManagement() {
  // Utilise le cache du contexte qui contient maintenant les rôles
  const { users: cachedUsers, loading: cacheLoading, refresh: refreshCache } = useUsersCache();
  const supabase = useSupabase();
  const [error, setError] = useState<Error | null>(null);

  /**
   * Gérer les erreurs de manière centralisée
   */
  function handleError(err: any, operation: string): void {
    const errorMessage = err.message || `Erreur lors de l'opération ${operation}`;
    console.error(`[useUsersManagement:${operation}]`, err);
    setError(new Error(errorMessage));
  }

  /**
   * Mapper les utilisateurs du cache vers le format User complet
   * Note: created_at et last_sign_in_at ne sont pas dans le cache (pas nécessaires pour l'affichage)
   */
  const users: User[] = useMemo(() => {
    return cachedUsers.map((user) => ({
      id: user.id,
      email: user.email || '',
      full_name: user.full_name || '-',
      role: (user.role as UserRole) || 'user',
      created_at: user.created_at ?? '',
      last_sign_in_at: user.last_sign_in_at ?? undefined,
      last_active_at: user.last_active_at ?? undefined,
    }));
  }, [cachedUsers]);

  /**
   * Rafraîchir la liste des utilisateurs
   */
  const fetchUsers = useCallback(async (): Promise<void> => {
    await refreshCache();
  }, [refreshCache]);

  /**
   * Créer un nouvel utilisateur
   */
  const createUser = useCallback(
    async (email: string, password: string, fullName: string, role: UserRole): Promise<boolean> => {
      try {
        setError(null);

        const { data, error: createError } = await supabase.rpc('create_user', {
          user_email: email,
          user_password: password,
          user_full_name: fullName,
          user_role: role,
        });

        if (createError) throw createError;
        if (!data?.success) throw new Error('Failed to create user');

        // Recharger le cache (qui mettra à jour automatiquement la liste locale)
        await refreshCache();

        // Envoyer l'email de bienvenue (non bloquant)
        supabase.functions
          .invoke('send-welcome-email', {
            body: {
              email,
              full_name: fullName,
              role,
            },
          })
          .then(({ data: emailData, error: emailError }) => {
            if (emailError) {
              console.warn('[createUser] Failed to send welcome email:', emailError);
            } else {
              console.warn('[createUser] Welcome email sent successfully:', emailData);
            }
          })
          .catch((err) => {
            console.warn('[createUser] Welcome email error:', err);
          });

        return true;
      } catch (err) {
        handleError(err, 'createUser');
        return false;
      }
    },
    [supabase, refreshCache]
  );

  /**
   * Mettre à jour le rôle d'un utilisateur
   */
  const updateUserRole = useCallback(
    async (userId: string, newRole: UserRole): Promise<boolean> => {
      try {
        setError(null);

        const { data, error: updateError } = await supabase.rpc('update_user_role', {
          target_user_id: userId,
          new_role: newRole,
        });

        if (updateError) throw updateError;
        if (!data?.success) throw new Error('Failed to update user role');

        // Recharger le cache pour refléter le changement
        await refreshCache();

        return true;
      } catch (err) {
        handleError(err, 'updateUserRole');
        return false;
      }
    },
    [supabase, refreshCache]
  );

  /**
   * Changer le mot de passe d'un utilisateur
   */
  const changeUserPassword = useCallback(
    async (userId: string, newPassword: string): Promise<boolean> => {
      try {
        setError(null);

        const { data, error: updateError } = await supabase.rpc('change_user_password', {
          target_user_id: userId,
          new_password: newPassword,
        });

        if (updateError) throw updateError;
        if (!data?.success) throw new Error('Failed to change password');

        return true;
      } catch (err) {
        handleError(err, 'changeUserPassword');
        return false;
      }
    },
    [supabase]
  );

  /**
   * Mettre à jour le nom complet d'un utilisateur
   */
  const updateUserFullName = useCallback(
    async (userId: string, newFullName: string): Promise<boolean> => {
      try {
        setError(null);

        const { data, error: updateError } = await supabase.rpc('update_user_full_name', {
          target_user_id: userId,
          new_full_name: newFullName,
        });

        if (updateError) throw updateError;
        if (!data?.success) throw new Error('Failed to update user name');

        await refreshCache();

        return true;
      } catch (err) {
        handleError(err, 'updateUserFullName');
        return false;
      }
    },
    [supabase, refreshCache]
  );

  /**
   * Supprimer un utilisateur
   */
  const deleteUser = useCallback(
    async (userId: string): Promise<boolean> => {
      try {
        setError(null);

        const { data, error: deleteError } = await supabase.rpc('delete_user', {
          target_user_id: userId,
        });

        if (deleteError) throw deleteError;
        if (!data?.success) throw new Error('Failed to delete user');

        // Recharger le cache pour refléter la suppression
        await refreshCache();

        return true;
      } catch (err) {
        handleError(err, 'deleteUser');
        return false;
      }
    },
    [supabase, refreshCache]
  );

  return {
    // API spécifique (pour compatibilité)
    users,
    loading: cacheLoading,
    error,
    refreshUsers: fetchUsers,
    fetchUsers,
    createUser,
    updateUserFullName,
    updateUserRole,
    changeUserPassword,
    deleteUser,

    // API générique (pour useEntityList) - note: update n'est pas standard car RPC
    data: users,
    create: createUser,
    remove: deleteUser,
  };
}

/**
 * MIGRATION GUIDE:
 *
 * ⚠️ CHANGEMENT DE NOM: useUsers() -> useUsersManagement()
 *
 * Ce hook est maintenant nommé useUsersManagement() pour éviter la confusion
 * avec le hook useUsers() du contexte qui est utilisé partout pour le cache.
 *
 * Le hook expose toujours les mêmes méthodes:
 * - users, loading, error
 * - fetchUsers(), createUser(), updateUserRole(), changeUserPassword(), deleteUser()
 *
 * 🎯 Améliorations:
 * - Utilise le cache du UsersContext (pas d'appel direct à get_users_list)
 * - Gestion d'erreurs centralisée
 * - useCallback pour éviter les re-renders
 * - Mises à jour optimistes
 * - Code plus lisible et maintainable
 */
